import { useState, useCallback, useEffect } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import { MapPin, X } from 'lucide-react';
import { AppInput } from '@/shared/components/ui/AppInput/AppInput';
import { AppSelect } from '@/shared/components/ui/AppSelect/AppSelect';
import { AppButton } from '@/shared/components/ui/AppButton/AppButton';
import { useDebouncedValue } from '@/shared/hooks/use-debounced-value';
import { AdPlatform } from '@/shared/types/enums';
import {
  audienceTargetingSchema,
  type AudienceTargetingFormValues,
} from '../../../schemas/audience-targeting.schema';
import {
  GOOGLE_AGE_RANGE_LABELS,
  type GOOGLE_AGE_RANGES,
  type LocationTarget,
  type InterestTarget,
  type Gender,
} from '../../../types/campaigns.types';
import {
  useLocationSearch,
  useInterestSearch,
  useBehaviorSearch,
} from '../../../api/campaigns-queries';
import {
  useGoogleAudienceSearch,
  useGoogleKeywordSearch,
  useGoogleGeoTargetSearch,
} from '../../../api/ads-google-queries';
import { getTargetingCapabilities } from '../../../logic/platform-constraints';
import { useCampaignWizardStore } from '../../../stores/campaign-wizard-store';
import { useWizardInvalidToast } from '../../../hooks/use-wizard-invalid-toast/use-wizard-invalid-toast';
import { SearchDropdown } from './SearchDropdown/SearchDropdown';
import styles from './AudienceTargetingStep.module.scss';

interface AudienceTargetingStepProps {
  onNext: () => void;
  onBack: () => void;
  onSaveDraft: () => void;
  isSaving: boolean;
}

const GENDER_OPTIONS: { value: Gender; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
];

/** Google's Reach Planner only accepts these fixed age brackets — no
 * arbitrary min/max like Meta. "All ages" listed first, as the sensible
 * default, rather than in the enum's declared order. */
const GOOGLE_AGE_RANGE_ORDER: (typeof GOOGLE_AGE_RANGES)[number][] = [
  'AGE_RANGE_18_65_UP',
  'AGE_RANGE_18_24',
  'AGE_RANGE_25_34',
  'AGE_RANGE_35_44',
  'AGE_RANGE_45_54',
  'AGE_RANGE_55_64',
  'AGE_RANGE_65_UP',
];
const GOOGLE_AGE_RANGE_OPTIONS = GOOGLE_AGE_RANGE_ORDER.map((value) => ({
  value,
  label: GOOGLE_AGE_RANGE_LABELS[value],
}));

/** Mirrors the `enabled: query.length >= 2` gate on the Meta search hooks. */
const SEARCH_MIN_CHARS = 2;

/**
 * Wizard Step 3: Define audience targeting parameters.
 */
export function AudienceTargetingStep({
  onNext,
  onBack,
  onSaveDraft,
  isSaving,
}: AudienceTargetingStepProps) {
  const { formData, updateFormData, markStepComplete } = useCampaignWizardStore();

  const [locationQuery, setLocationQuery] = useState('');
  const [interestQuery, setInterestQuery] = useState('');
  const [keywordInput, setKeywordInput] = useState('');
  const [behaviorQuery, setBehaviorQuery] = useState('');

  // `isGoogle` picks the search data source (Google Ads API vs Meta search);
  // feature visibility is gated by the capabilities map instead.
  const isGoogle = formData.platform === AdPlatform.GOOGLE;
  const capabilities = getTargetingCapabilities(formData.platform);
  const adAccountId = formData.adAccountId;

  const debouncedLocationQuery = useDebouncedValue(locationQuery, 300);
  const debouncedInterestQuery = useDebouncedValue(interestQuery, 300);
  const debouncedKeywordQuery = useDebouncedValue(keywordInput, 300);
  const debouncedBehaviorQuery = useDebouncedValue(behaviorQuery, 300);

  // Trimmed terms drive the Meta searches so the `>= 2` gate inside the query
  // hooks lines up with the threshold that opens the dropdown, and so incidental
  // whitespace doesn't fragment the query cache.
  const locationTerm = locationQuery.trim();
  const interestTerm = interestQuery.trim();
  const behaviorTerm = behaviorQuery.trim();
  const debouncedLocationTerm = debouncedLocationQuery.trim();
  const debouncedInterestTerm = debouncedInterestQuery.trim();
  const debouncedBehaviorTerm = debouncedBehaviorQuery.trim();

  // Locations come from whichever catalog the target platform can actually
  // launch with: Meta geo keys are not valid Google criteria (and vice versa),
  // so picking from the wrong one silently drops the targeting at launch.
  const {
    data: metaLocationResults,
    isFetching: metaLocationLoading,
    isError: metaLocationError,
  } = useLocationSearch(isGoogle ? '' : debouncedLocationTerm);
  const {
    data: googleLocationResults,
    isFetching: googleLocationLoading,
    isError: googleLocationError,
  } = useGoogleGeoTargetSearch(adAccountId, isGoogle ? debouncedLocationTerm : '');
  const locationResults = isGoogle ? googleLocationResults : metaLocationResults;
  const locationLoading = isGoogle ? googleLocationLoading : metaLocationLoading;
  const locationError = isGoogle ? googleLocationError : metaLocationError;
  const {
    data: genericInterestResults,
    isFetching: genericInterestLoading,
    isError: genericInterestError,
  } = useInterestSearch(isGoogle ? '' : debouncedInterestTerm);
  const {
    data: behaviorResults,
    isFetching: behaviorLoading,
    isError: behaviorError,
  } = useBehaviorSearch(capabilities.behaviors ? debouncedBehaviorTerm : '');
  const {
    data: googleAudienceResults,
    isFetching: googleAudienceLoading,
    isError: googleAudienceError,
  } = useGoogleAudienceSearch(adAccountId, isGoogle ? debouncedInterestQuery : '');
  const {
    data: googleKeywordResults,
    isFetching: googleKeywordLoading,
    isError: googleKeywordError,
  } = useGoogleKeywordSearch(adAccountId, isGoogle ? debouncedKeywordQuery : '');

  // The dropdown opens off the raw term but the request only fires off the
  // debounced one, so the window in between has to count as "searching" —
  // otherwise it renders "no results" for a search that hasn't started yet.
  const isLocationSearching = locationLoading || locationTerm !== debouncedLocationTerm;
  const isInterestSearching = genericInterestLoading || interestTerm !== debouncedInterestTerm;
  const isBehaviorSearching = behaviorLoading || behaviorTerm !== debouncedBehaviorTerm;

  // Spread-merge so persisted drafts that predate newer fields (behaviors)
  // still get their defaults.
  const defaultTargeting = {
    locations: [],
    ageMin: 18,
    ageMax: 65,
    googleAgeRange: 'AGE_RANGE_18_65_UP' as const,
    genders: ['all' as Gender],
    interests: [],
    keywords: [],
    behaviors: [],
    ...formData.targetingConfig,
  };

  const {
    control,
    handleSubmit,
    watch,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<AudienceTargetingFormValues>({
    resolver: zodResolver(audienceTargetingSchema),
    defaultValues: {
      targetingConfig: defaultTargeting,
    },
  });

  const onInvalidSubmit = useWizardInvalidToast();

  const watchedConfig = watch('targetingConfig');

  // Keep the Audience Estimate (wizard right rail) — and the store that
  // "Save as Draft" reads from — live as the user edits, not just on
  // Continue. RHF's watch(callback) fires on field changes (not on every
  // render), so writing to the store here can't loop with re-renders. This
  // write is synchronous: debouncing it let a "Save as Draft" click race
  // ahead of a still-pending sync and persist stale data. The audience
  // estimate query itself still debounces (see AudienceEstimatePanel), so
  // this doesn't cause extra network traffic.
  useEffect(() => {
    const subscription = watch(() => {
      updateFormData({ targetingConfig: getValues('targetingConfig') });
    });
    return () => subscription.unsubscribe();
  }, [watch, getValues, updateFormData]);

  const {
    fields: locationFields,
    append: appendLocation,
    remove: removeLocation,
  } = useFieldArray({ control, name: 'targetingConfig.locations' });

  const {
    fields: interestFields,
    append: appendInterest,
    remove: removeInterest,
  } = useFieldArray({ control, name: 'targetingConfig.interests' });

  const {
    fields: behaviorFields,
    append: appendBehavior,
    remove: removeBehavior,
  } = useFieldArray({ control, name: 'targetingConfig.behaviors' });

  const handleAddLocation = useCallback(
    (location: LocationTarget) => {
      const alreadyAdded = locationFields.some((l) => l.id === location.id);
      if (!alreadyAdded) {
        appendLocation(location);
      }
      setLocationQuery('');
    },
    [locationFields, appendLocation],
  );

  const handleAddInterest = useCallback(
    (interest: InterestTarget) => {
      const alreadyAdded = interestFields.some((i) => i.id === interest.id);
      if (!alreadyAdded) {
        appendInterest(interest);
      }
      setInterestQuery('');
    },
    [interestFields, appendInterest],
  );

  const handleAddBehavior = useCallback(
    (behavior: InterestTarget) => {
      const alreadyAdded = behaviorFields.some((b) => b.id === behavior.id);
      if (!alreadyAdded) {
        appendBehavior(behavior);
      }
      setBehaviorQuery('');
    },
    [behaviorFields, appendBehavior],
  );

  const handleAddKeyword = useCallback(() => {
    const keyword = keywordInput.trim();
    if (keyword && !(watchedConfig.keywords ?? []).includes(keyword)) {
      setValue('targetingConfig.keywords', [...(watchedConfig.keywords ?? []), keyword]);
    }
    setKeywordInput('');
  }, [keywordInput, watchedConfig.keywords, setValue]);

  const handleRemoveKeyword = useCallback(
    (keyword: string) => {
      setValue(
        'targetingConfig.keywords',
        (watchedConfig.keywords ?? []).filter((k) => k !== keyword),
      );
    },
    [watchedConfig.keywords, setValue],
  );

  const handleGenderToggle = useCallback(
    (gender: Gender) => {
      const current = watchedConfig.genders ?? [];
      if (gender === 'all') {
        setValue('targetingConfig.genders', ['all']);
      } else {
        const withoutAll = current.filter((g) => g !== 'all');
        const isSelected = withoutAll.includes(gender);
        const next = isSelected ? withoutAll.filter((g) => g !== gender) : [...withoutAll, gender];
        setValue('targetingConfig.genders', next.length === 0 ? ['all'] : next);
      }
    },
    [watchedConfig.genders, setValue],
  );

  const onSubmit = (data: AudienceTargetingFormValues) => {
    updateFormData(data);
    markStepComplete('audience-targeting');
    onNext();
  };

  return (
    <form
      className={styles.step}
      onSubmit={handleSubmit(onSubmit, onInvalidSubmit)}
      onKeyDown={(e) => {
        // Only the Continue button should submit the form; Enter in any
        // field (search boxes, age inputs, keyword input) must not advance
        // the wizard on its own.
        if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'BUTTON') {
          e.preventDefault();
        }
      }}
      noValidate
    >
      {/* Location Targeting */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Location Targeting</h2>
        <p className={styles.sectionDescription}>
          Choose the geographic areas where your ads will be shown.
        </p>

        <div className={styles.searchWrapper}>
          <AppInput
            label="Search locations"
            placeholder="Search by country, city, or region..."
            value={locationQuery}
            onChange={(e) => setLocationQuery(e.target.value)}
          />
          <SearchDropdown
            isOpen={locationTerm.length >= SEARCH_MIN_CHARS}
            isLoading={isLocationSearching}
            isError={locationError}
            isEmpty={locationResults?.length === 0}
            loadingLabel="Searching locations..."
            errorLabel="Could not load locations. Please try again."
            emptyLabel={`No locations match “${debouncedLocationTerm}”.`}
          >
            {locationResults?.map((loc) => (
              <button
                key={loc.id}
                type="button"
                className={styles.searchItem}
                onClick={() => handleAddLocation(loc)}
              >
                <MapPin size={14} />
                <span>{loc.name}</span>
                <span className={styles.locationType}>{loc.type}</span>
              </button>
            ))}
          </SearchDropdown>
        </div>

        <div className={styles.tagList}>
          {locationFields.map((loc, index) => (
            <span key={loc.id} className={styles.tag}>
              <MapPin size={12} />
              {loc.name}
              <button
                type="button"
                className={styles.tagRemove}
                onClick={() => removeLocation(index)}
                aria-label={`Remove ${loc.name}`}
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
        {errors.targetingConfig?.locations && (
          <span className={styles.error} role="alert">
            {errors.targetingConfig.locations.message}
          </span>
        )}
      </div>

      {/* Age Range */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Age Range</h2>
        {isGoogle ? (
          // Google's Reach Planner only accepts one of a fixed set of age
          // brackets — no arbitrary min/max like Meta — so Google gets a
          // single-select instead of the two number inputs below.
          <Controller
            name="targetingConfig.googleAgeRange"
            control={control}
            render={({ field }) => (
              <AppSelect
                label="Age range"
                options={GOOGLE_AGE_RANGE_OPTIONS}
                value={field.value}
                onValueChange={field.onChange}
                error={errors.targetingConfig?.googleAgeRange?.message}
              />
            )}
          />
        ) : (
          <div className={styles.ageRow}>
            <Controller
              name="targetingConfig.ageMin"
              control={control}
              render={({ field }) => (
                <AppInput
                  label="Min Age"
                  type="number"
                  min={13}
                  max={65}
                  value={field.value}
                  onChange={(e) => field.onChange(Number(e.target.value))}
                  error={errors.targetingConfig?.ageMin?.message}
                />
              )}
            />
            <span className={styles.ageSeparator}>to</span>
            <Controller
              name="targetingConfig.ageMax"
              control={control}
              render={({ field }) => (
                <AppInput
                  label="Max Age"
                  type="number"
                  min={18}
                  max={65}
                  value={field.value}
                  onChange={(e) => field.onChange(Number(e.target.value))}
                  error={errors.targetingConfig?.ageMax?.message}
                />
              )}
            />
          </div>
        )}
      </div>

      {/* Gender Selection */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Gender</h2>
        <div className={styles.genderOptions}>
          {GENDER_OPTIONS.map((opt) => {
            const isSelected = (watchedConfig.genders ?? []).includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                className={clsx(styles.genderOption, isSelected && styles.genderSelected)}
                onClick={() => handleGenderToggle(opt.value)}
                aria-pressed={isSelected}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
        {errors.targetingConfig?.genders && (
          <span className={styles.error} role="alert">
            {errors.targetingConfig.genders.message}
          </span>
        )}
      </div>

      {/* Interest / Audience Targeting */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>
          {isGoogle ? 'Audience Targeting' : 'Interest Targeting'}
        </h2>
        <p className={styles.sectionDescription}>
          {isGoogle
            ? 'Target Google Ads audience segments and user interests pulled live from the Google Ads API.'
            : 'Target people based on their interests, hobbies, and activities.'}
        </p>

        <div className={styles.searchWrapper}>
          <AppInput
            label={isGoogle ? 'Search audiences' : 'Search interests'}
            placeholder={isGoogle ? 'Type to search Google audiences...' : 'Search interests...'}
            value={interestQuery}
            onChange={(e) => setInterestQuery(e.target.value)}
          />
          {isGoogle ? (
            <SearchDropdown
              isOpen={debouncedInterestQuery.length >= 1}
              isLoading={googleAudienceLoading}
              isError={googleAudienceError}
              isEmpty={googleAudienceResults?.length === 0}
              loadingLabel="Searching Google audiences..."
              errorLabel="Could not load suggestions. Check your Google Ads connection and try again."
              emptyLabel={`No audiences match “${debouncedInterestQuery}”.`}
            >
              {googleAudienceResults?.map((audience) => (
                <button
                  key={`${audience.type}-${audience.id}`}
                  type="button"
                  className={styles.searchItem}
                  onClick={() =>
                    handleAddInterest({
                      id: audience.id,
                      name: audience.displayName,
                      category: audience.type === 'USER_INTEREST' ? 'Interest' : 'Audience',
                      type: audience.type,
                    })
                  }
                >
                  <span>{audience.displayName}</span>
                  <span className={styles.locationType}>
                    {audience.type === 'USER_INTEREST' ? 'Interest' : 'Audience'}
                  </span>
                </button>
              ))}
            </SearchDropdown>
          ) : (
            <SearchDropdown
              isOpen={interestTerm.length >= SEARCH_MIN_CHARS}
              isLoading={isInterestSearching}
              isError={genericInterestError}
              isEmpty={genericInterestResults?.length === 0}
              loadingLabel="Searching interests..."
              errorLabel="Could not load interests. Please try again."
              emptyLabel={`No interests match “${debouncedInterestTerm}”.`}
            >
              {genericInterestResults?.map((interest) => (
                <button
                  key={interest.id}
                  type="button"
                  className={styles.searchItem}
                  onClick={() => handleAddInterest(interest)}
                >
                  <span>{interest.name}</span>
                  {interest.category && (
                    <span className={styles.locationType}>{interest.category}</span>
                  )}
                </button>
              ))}
            </SearchDropdown>
          )}
        </div>

        <div className={styles.tagList}>
          {interestFields.map((interest, index) => (
            <span key={interest.id} className={styles.tag}>
              {interest.name}
              <button
                type="button"
                className={styles.tagRemove}
                onClick={() => removeInterest(index)}
                aria-label={`Remove ${interest.name}`}
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      </div>

      {/* Behavior Targeting (Meta) */}
      {capabilities.behaviors && (
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Behavior Targeting</h2>
          <p className={styles.sectionDescription}>
            Target people based on purchase behaviors, device usage, and travel patterns.
          </p>

          <div className={styles.searchWrapper}>
            <AppInput
              label="Search behaviors"
              placeholder="Search behaviors (e.g. frequent travelers)..."
              value={behaviorQuery}
              onChange={(e) => setBehaviorQuery(e.target.value)}
            />
            <SearchDropdown
              isOpen={behaviorTerm.length >= SEARCH_MIN_CHARS}
              isLoading={isBehaviorSearching}
              isError={behaviorError}
              isEmpty={behaviorResults?.length === 0}
              loadingLabel="Searching behaviors..."
              errorLabel="Could not load behaviors. Please try again."
              emptyLabel={`No behaviors match “${debouncedBehaviorTerm}”.`}
            >
              {behaviorResults?.map((behavior) => (
                <button
                  key={behavior.id}
                  type="button"
                  className={styles.searchItem}
                  onClick={() => handleAddBehavior({ ...behavior, type: 'BEHAVIOR' })}
                >
                  <span>{behavior.name}</span>
                  {behavior.category && (
                    <span className={styles.locationType}>{behavior.category}</span>
                  )}
                </button>
              ))}
            </SearchDropdown>
          </div>

          <div className={styles.tagList}>
            {behaviorFields.map((behavior, index) => (
              <span key={behavior.id} className={styles.tag}>
                {behavior.name}
                <button
                  type="button"
                  className={styles.tagRemove}
                  onClick={() => removeBehavior(index)}
                  aria-label={`Remove ${behavior.name}`}
                >
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Keyword Targeting */}
      {capabilities.keywords && (
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Keyword Targeting</h2>
          <p className={styles.sectionDescription}>
            {isGoogle
              ? 'Search Google Ads keyword ideas — competition and average monthly searches help you pick high-intent terms.'
              : 'Add keywords to target people searching for related terms.'}
          </p>

          {isGoogle ? (
            <div className={styles.searchWrapper}>
              <AppInput
                label="Search keyword ideas"
                placeholder="Type a seed keyword (e.g. concert tickets)..."
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
              />
              <SearchDropdown
                isOpen={debouncedKeywordQuery.length >= 1}
                isLoading={googleKeywordLoading}
                isError={googleKeywordError}
                isEmpty={googleKeywordResults?.length === 0}
                loadingLabel="Searching keyword ideas..."
                errorLabel="Could not load keyword ideas. Check your Google Ads connection and try again."
                emptyLabel={`No keyword ideas match “${debouncedKeywordQuery}”.`}
              >
                {googleKeywordResults?.map((suggestion) => {
                  const alreadyAdded = (watchedConfig.keywords ?? []).includes(suggestion.text);
                  return (
                    <button
                      key={suggestion.text}
                      type="button"
                      className={styles.searchItem}
                      disabled={alreadyAdded}
                      onClick={() => {
                        if (!alreadyAdded) {
                          setValue('targetingConfig.keywords', [
                            ...(watchedConfig.keywords ?? []),
                            suggestion.text,
                          ]);
                        }
                        setKeywordInput('');
                      }}
                    >
                      <span>{suggestion.text}</span>
                      <span className={styles.locationType}>
                        {suggestion.avgMonthlySearches.toLocaleString()} / mo ·{' '}
                        {suggestion.competition}
                      </span>
                    </button>
                  );
                })}
              </SearchDropdown>
            </div>
          ) : (
            <div className={styles.keywordInput}>
              <AppInput
                placeholder="Type a keyword and press Enter..."
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddKeyword();
                  }
                }}
              />
              <AppButton
                type="button"
                variant="secondary"
                size="sm"
                onClick={handleAddKeyword}
                disabled={!keywordInput.trim()}
              >
                Add
              </AppButton>
            </div>
          )}

          <div className={styles.tagList}>
            {(watchedConfig.keywords ?? []).map((keyword) => (
              <span key={keyword} className={styles.tag}>
                {keyword}
                <button
                  type="button"
                  className={styles.tagRemove}
                  onClick={() => handleRemoveKeyword(keyword)}
                  aria-label={`Remove ${keyword}`}
                >
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      <div className={styles.actions}>
        <AppButton type="button" variant="ghost" onClick={onSaveDraft} loading={isSaving}>
          Save as Draft
        </AppButton>
        <div className={styles.navActions}>
          <AppButton type="button" variant="secondary" onClick={onBack}>
            Back
          </AppButton>
          <AppButton type="submit">Continue</AppButton>
        </div>
      </div>
    </form>
  );
}
